
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, BarChart2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CardTable from './cards/CardTable';
import CardForm from './cards/CardForm';
import { useCardManagement } from './cards/useCardManagement';
import PollWidgetEmpty from '../polls/PollWidgetEmpty';
import { useIsMobile } from '@/hooks/use-mobile';
import { useInsightCards } from '@/hooks/admin/cards/useInsightCards';
import { useInsightsPagination } from '@/hooks/admin/cards/useInsightsPagination';
import InsightFilters from './cards/InsightFilters';
import InsightsPagination from './cards/InsightsPagination';

const CardDataManagement = () => {
  const {
    cards,
    isLoading,
    dialogOpen,
    setDialogOpen,
    currentCard,
    setCurrentCard,
    isEditing,
    handleOpenDialog,
    handleSaveCard,
    handleDeleteCard
  } = useCardManagement();
  
  const { allCards, isLoadingCards } = useInsightCards();
  const isMobile = useIsMobile();

  // Use our pagination hook
  const {
    paginatedCards,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    countryFilter,
    setCountryFilter,
    categoryFilter,
    setCategoryFilter,
    resetFilters
  } = useInsightsPagination({ 
    cards: allCards, 
    initialItemsPerPage: 10
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart2 size={20} />
            Insight Card Management
          </CardTitle>
          <CardDescription>
            Manage survey cards and poll data shown on the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`mb-4 ${isMobile ? 'flex flex-col space-y-4' : 'flex justify-between'}`}>
            <InsightFilters 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              countryFilter={countryFilter}
              setCountryFilter={setCountryFilter}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              onClearFilters={resetFilters}
            />
            
            <Button 
              onClick={() => handleOpenDialog()}
              className={isMobile ? 'w-full' : ''}
            >
              <Plus size={16} className="mr-2" />
              Add New Card
            </Button>
          </div>
          
          {isLoading || isLoadingCards ? (
            <div className="flex justify-center py-8">
              <p>Loading cards...</p>
            </div>
          ) : paginatedCards.length === 0 ? (
            <PollWidgetEmpty message="No insight cards found. Create your first card using the button above." />
          ) : (
            <>
              <CardTable 
                cards={paginatedCards}
                isLoading={isLoading} 
                onEdit={handleOpenDialog}
                onDelete={handleDeleteCard}
              />
              
              <InsightsPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                startIndex={startIndex}
                endIndex={endIndex}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95%] p-4' : 'sm:max-w-[600px]'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit' : 'Create'} Insight Card</DialogTitle>
          </DialogHeader>
          
          {currentCard && (
            <CardForm
              currentCard={currentCard}
              setCurrentCard={setCurrentCard}
              onCancel={() => setDialogOpen(false)}
              onSave={handleSaveCard}
              isEditing={isEditing}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CardDataManagement;
